import { LEVEL_CONFIGS, MAX_GAMIFICATION_LEVEL } from "../../constants/gamification/levelConfigs";
import { type LevelConfig, type LevelProgressResult } from "../../types/gamification/level";

const firstLevelConfig = LEVEL_CONFIGS[0];

export const getLevelForPoints = (points: number): LevelConfig => {
  if (!firstLevelConfig) {
    throw new Error("LEVEL_CONFIGS must define at least one level");
  }

  const safePoints = Math.max(0, points);
  let current = firstLevelConfig;

  for (const config of LEVEL_CONFIGS) {
    if (safePoints >= config.pointsRequired) {
      current = config;
    } else {
      break;
    }
  }

  return current;
};

export const getLevelProgress = (points: number): LevelProgressResult => {
  const safePoints = Math.max(0, points);
  const current = getLevelForPoints(safePoints);

  if (current.level >= MAX_GAMIFICATION_LEVEL) {
    return { progress: 1, pointsToNext: 0 };
  }

  const currentMin = current.pointsRequired;
  const nextConfig = LEVEL_CONFIGS[current.level];

  if (!nextConfig) {
    return { progress: 1, pointsToNext: 0 };
  }

  const nextMin = nextConfig.pointsRequired;
  const span = nextMin - currentMin;

  if (span <= 0) {
    return { progress: 1, pointsToNext: 0 };
  }

  const progress = Math.min(1, Math.max(0, (safePoints - currentMin) / span));
  const pointsToNext = Math.max(0, nextMin - safePoints);

  return { progress, pointsToNext };
};

/**
 * Minimum total points required to be at the given level (from `LEVEL_CONFIGS`).
 */
export const getPointsRequiredForLevel = (level: number): number => {
  const config = LEVEL_CONFIGS.find((entry) => entry.level === level);

  if (!config) {
    throw new Error(`Unknown gamification level ${String(level)}`);
  }

  return config.pointsRequired;
};

/**
 * Total points just below the threshold for `level` (useful for level-up QA seeds).
 */
export const getPointsJustBelowLevel = (level: number, gap = 1): number => {
  if (level <= 1) {
    throw new Error("getPointsJustBelowLevel requires level ≥ 2");
  }

  return Math.max(0, getPointsRequiredForLevel(level) - Math.max(1, gap));
};
